/**
 * Custom hazard pins (Diurnal parity 05-022, 05-023; docs/specs/map.md §16.12; decision 620): pins the crew place on
 * the map for a house, cattle yards, an airstrip, a tower and so on, kept as shared records of the project
 * (`hazard_pin`, Teslyn Field migration 0049, Postgres `20261027005000_hazard_pins.sql`) so every laptop of the
 * project and Teslyn Cloud have them.
 *
 * As the Diurnal tool (aircraft-map.js am:28170-28934): a pin is placed by a click on the map or from typed
 * coordinates, named ("Hazard" to start), with a description, one of 10 icons and one of 13 colours (choosing an
 * icon takes its own colour); it is dragged to move, edited, listed with show or hide, zoom (to 14) and remove, and a
 * saved hazard map holds it with the same icon. Changed from the tool: one position box takes every form crews copy
 * (decimal, degrees and minutes, DMS or UTM, `parseCoordinates`), names and descriptions are text, never markup (the
 * tool's tooltips and list were unescaped), and hiding a pin is this laptop's choice, not the shared record's.
 *
 * Each pin may belong to a hazard map (`hazard_map`, the map's name, e.g. `Hazards_F26048_Milgun_fnl_BND`), as the
 * tool kept pins per hazard layer; a pin with no map belongs to the project. A saved hazard map holds its own pins
 * and the project's. A map made again from the same boundary has the same name, so its pins stay with it, with their
 * icons and colours (the tool's bug 05-2).
 */
import data from './hazard-pin-icons.json';
import { latLngToUtm, parseCoordinates, utmWords } from './coordinates.js';
import type { HazardPin as FilePin, HazardSet } from './map-kml.js';
import type { Common, Uuid } from './model.js';
import type { LatLng } from './planning.js';

export type PinIconKey =
  | 'house'
  | 'cattle'
  | 'airfield'
  | 'tower'
  | 'blasting'
  | 'drone'
  | 'helicopter'
  | 'dam'
  | 'warning'
  | 'fuelcache';

export interface PinIcon {
  readonly key: PinIconKey;
  /** "Fuel Cache", as the tool's grid. */
  readonly label: string;
  /** A 24 by 24 SVG path, drawn in white on the pin's colour. */
  readonly path: string;
  readonly defaultColour: string;
}

/** The 10 icons, in the tool's order (am:577-588). */
export const PIN_ICONS: readonly PinIcon[] = data.icons as PinIcon[];

/** The 13 colours, in the tool's order (am:590-594). */
export const PIN_COLOURS: readonly string[] = data.colours;

/** Each colour in words, for the colour buttons and the list (a colour is never shown by itself). */
export const PIN_COLOUR_WORDS: Readonly<Record<string, string>> = {
  '#f44336': 'Red',
  '#ff5722': 'Deep orange',
  '#ff9800': 'Orange',
  '#ffc107': 'Amber',
  '#4caf50': 'Green',
  '#009688': 'Teal',
  '#2196f3': 'Blue',
  '#3f51b5': 'Indigo',
  '#9c27b0': 'Purple',
  '#e91e63': 'Pink',
  '#795548': 'Brown',
  '#8d6e63': 'Light brown',
  '#607d8b': 'Blue grey',
};

const DEFAULT_ICON: PinIconKey = 'warning';

/**
 * The ink of a pin's drawing: its icon and edge, white on the pin's colour on every base map and theme, as the saved
 * hazard KML's icon (am:28390, am:28745). Part of the pin's look, like its colour, not of the app's theme.
 */
export const PIN_ICON_INK = '#ffffff';

/** An icon by key; an unknown key (an older file) is the warning icon, as the tool. */
export function pinIcon(key: string): PinIcon {
  return PIN_ICONS.find((i) => i.key === key) ?? PIN_ICONS.find((i) => i.key === DEFAULT_ICON)!;
}

const HEX = /^#[0-9a-f]{6}$/;

/** The colour a pin is drawn in: its own (`#rrggbb`), else its icon's. */
export function pinColour(icon: string, colour: string): string {
  const c = colour.trim().toLowerCase();
  return HEX.test(c) ? c : pinIcon(icon).defaultColour;
}

/** A colour in words: one of the 13 by name, any other as its code. */
export function pinColourWord(colour: string): string {
  return PIN_COLOUR_WORDS[colour.toLowerCase()] ?? colour;
}

/** A shared hazard pin (`hazard_pin`). Teslyn Cloud adds tenant_id and placed_by (the sign-in). */
export interface HazardPinRecord extends Common {
  readonly project_id: Uuid;
  /** The hazard map it belongs to (its name), or '' for the project. */
  readonly hazard_map: string;
  readonly name: string;
  readonly description: string;
  readonly lat: number;
  readonly lng: number;
  readonly icon: PinIconKey;
  /** `#rrggbb`. */
  readonly colour: string;
  /** Who placed it (Teslyn Cloud writes the name from the staff register). */
  readonly placed_by_name: string;
}

export const PIN_NAME_MAX = 200;
export const PIN_DESCRIPTION_MAX = 2000;

/** The pin form: what is typed and chosen. */
export interface PinDraft {
  readonly name: string;
  readonly description: string;
  /** The position as typed: any form `parseCoordinates` reads. */
  readonly position: string;
  readonly icon: PinIconKey;
  readonly colour: string;
  readonly hazard_map: string;
}

/** A position in the box: 6 decimals (about 0.1 m), as the tool filled it. */
export function pinPositionText(at: LatLng): string {
  return `${at.lat.toFixed(6)}, ${at.lng.toFixed(6)}`;
}

/** A new pin: "Hazard", the warning icon in its colour, at the click (or empty for typed coordinates). */
export function newPinDraft(at: LatLng | null, hazardMap = ''): PinDraft {
  const icon = pinIcon(DEFAULT_ICON);
  return {
    name: 'Hazard',
    description: '',
    position: at ? pinPositionText(at) : '',
    icon: icon.key,
    colour: icon.defaultColour,
    hazard_map: hazardMap,
  };
}

/** The form for an existing pin. */
export function pinDraftOf(p: HazardPinRecord): PinDraft {
  return {
    name: p.name,
    description: p.description,
    position: pinPositionText(p),
    icon: pinIcon(p.icon).key,
    colour: pinColour(p.icon, p.colour),
    hazard_map: p.hazard_map,
  };
}

/** Choosing an icon also chooses its colour (the tool's grid); a colour chosen afterwards stays. */
export function pinDraftWithIcon(d: PinDraft, icon: PinIconKey): PinDraft {
  return { ...d, icon, colour: pinIcon(icon).defaultColour };
}

/** What a checked form saves. */
export interface PinValues {
  readonly name: string;
  readonly description: string;
  readonly lat: number;
  readonly lng: number;
  readonly icon: PinIconKey;
  readonly colour: string;
  readonly hazard_map: string;
}

export type PinDraftCheck =
  | { readonly ok: true; readonly value: PinValues }
  | {
      readonly ok: false;
      readonly errors: { readonly name?: string; readonly position?: string; readonly description?: string };
    };

/** Check the form: a name, a position Teslyn can read, a description that fits. */
export function checkPinDraft(d: PinDraft): PinDraftCheck {
  const errors: { name?: string; position?: string; description?: string } = {};
  const name = d.name.trim();
  const description = d.description.trim();
  if (name === '') errors.name = 'Type a name for the pin';
  else if (name.length > PIN_NAME_MAX) errors.name = `Keep the name to ${PIN_NAME_MAX} characters`;
  if (description.length > PIN_DESCRIPTION_MAX) {
    errors.description = `Keep the description to ${PIN_DESCRIPTION_MAX} characters`;
  }
  const at = parseCoordinates(d.position);
  if (!at.ok) errors.position = at.error;
  if (errors.name || errors.position || errors.description || !at.ok) return { ok: false, errors };
  return {
    ok: true,
    value: {
      name,
      description,
      lat: at.at.lat,
      lng: at.at.lng,
      icon: pinIcon(d.icon).key,
      colour: pinColour(d.icon, d.colour),
      hazard_map: d.hazard_map,
    },
  };
}

/** A pin's position in words: decimal degrees and UTM in its own zone ("-26.6118, 118.5420 · 50J …"). */
export function pinPositionWords(at: LatLng): string {
  return `${at.lat.toFixed(6)}, ${at.lng.toFixed(6)} · ${utmWords(latLngToUtm(at))}`;
}

/** Where a pin belongs, in words. */
export function pinMapWords(hazardMap: string): string {
  return hazardMap === '' ? 'The project (every hazard map)' : hazardMap;
}

/** The hover text of a pin on the map. */
export function pinTooltip(p: Pick<HazardPinRecord, 'name' | 'icon' | 'description'>): string {
  return [`Hazard pin · ${p.name} (${pinIcon(p.icon).label})`, p.description]
    .filter((x) => x !== '')
    .join(' · ');
}

/** The identify rows of a pin. */
export function pinIdentifyRows(
  p: HazardPinRecord,
): ReadonlyArray<{ readonly label: string; readonly value: string }> {
  return [
    { label: 'Hazard pin', value: p.name },
    { label: 'Icon', value: `${pinIcon(p.icon).label}, ${pinColourWord(pinColour(p.icon, p.colour))}` },
    ...(p.description ? [{ label: 'Description', value: p.description }] : []),
    { label: 'Position', value: pinPositionWords(p) },
    { label: 'Hazard map', value: pinMapWords(p.hazard_map) },
    ...(p.placed_by_name ? [{ label: 'Placed by', value: p.placed_by_name }] : []),
  ];
}

/** The live pins in list order: by hazard map (the project's first), then name. */
export function pinsInListOrder(pins: readonly HazardPinRecord[]): HazardPinRecord[] {
  return pins
    .filter((p) => p.deleted_at === null)
    .sort(
      (a, b) =>
        a.hazard_map.localeCompare(b.hazard_map) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
    );
}

/** The pins a hazard map holds when saved: its own and the project's. */
export function pinsOfMap(pins: readonly HazardPinRecord[], mapName: string): HazardPinRecord[] {
  return pinsInListOrder(pins).filter((p) => p.hazard_map === '' || p.hazard_map === mapName);
}

/** Positions closer than this (about 1 cm) are the same pin. */
const SAME_PLACE_DEG = 1e-7;

const samePin = (a: { name: string; lat: number; lng: number }, b: FilePin): boolean =>
  a.name === b.name && Math.abs(a.lat - b.lat) < SAME_PLACE_DEG && Math.abs(a.lng - b.lng) < SAME_PLACE_DEG;

/**
 * The pins inside a hazard file (a Diurnal file, or one saved before pins were shared) that are not shared pins of
 * the project yet: they are drawn from the file, and "Share these pins" makes them shared pins.
 */
export function filePinsNotShared(set: HazardSet, shared: readonly HazardPinRecord[]): FilePin[] {
  const live = shared.filter((p) => p.deleted_at === null);
  return set.pins.filter((fp) => !live.some((p) => samePin(p, fp)));
}

/** A file's pins as shared pins of that hazard map (`filePinsNotShared` first). */
export function pinValuesOfFile(set: HazardSet, pins: readonly FilePin[]): PinValues[] {
  return pins.map((fp) => {
    const icon = pinIcon(fp.icon_type).key;
    return {
      name: (fp.name.trim() || 'Hazard').slice(0, PIN_NAME_MAX),
      description: fp.description.trim().slice(0, PIN_DESCRIPTION_MAX),
      lat: fp.lat,
      lng: fp.lng,
      icon,
      colour: pinColour(icon, fp.color),
      hazard_map: set.name,
    };
  });
}

/**
 * The pins written into a saved hazard map (`hazard_save`): the map's shared pins and the project's, then the file's
 * own pins not shared yet, numbered from 1 as the Diurnal tool's `customPinsJson`. A pin hidden on this laptop
 * (`hidden`: the map's hidden folders, `pinHiddenKey`) is saved hidden.
 */
export function pinsForSaving(
  set: HazardSet,
  shared: readonly HazardPinRecord[],
  hidden: ReadonlySet<string> = new Set(),
): FilePin[] {
  const mine = pinsOfMap(shared, set.name).map((p) => ({
    name: p.name,
    lat: p.lat,
    lng: p.lng,
    description: p.description,
    icon_type: pinIcon(p.icon).key as string,
    color: pinColour(p.icon, p.colour),
    visible: !hidden.has(pinHiddenKey(p.id)),
  }));
  const own = filePinsNotShared(set, shared).map((p) => ({
    name: p.name,
    lat: p.lat,
    lng: p.lng,
    description: p.description,
    icon_type: pinIcon(p.icon_type).key as string,
    color: pinColour(p.icon_type, p.color),
    visible: p.visible,
  }));
  return [...mine, ...own].map((p, i) => ({ id: i + 1, ...p }));
}

/** The key of a pin hidden on this laptop, in the map's hidden folders (`hidden_hazard_folders`). */
export const pinHiddenKey = (id: string): string => `pin:${id}`;

// ------------------------------------------------------------------------------------------------ the recce map (R157)

/**
 * A hazard added on the phone's recce map (MB's R157; decisions 620, 778, 871): the pilot or crew taps "Add a
 * hazard", places it at the map's centre (or the phone's own position), chooses one of the same 10 icons and types
 * notes. It is saved as an ordinary shared pin of the project (`hazard_map` ''), so every laptop, Teslyn Cloud's maps
 * and the pilot hazard pack see it like a pin placed in Teslyn Field; the database stamps who placed it. A name is
 * optional: without one the pin is named by its icon ("Tower").
 */
export interface RecceDraft {
  readonly icon: PinIconKey;
  readonly name: string;
  readonly notes: string;
}

export const newRecceDraft = (): RecceDraft => ({ icon: DEFAULT_ICON, name: '', notes: '' });

/** The pin's name: what was typed, else the icon's label. */
export const recceName = (d: Pick<RecceDraft, 'icon' | 'name'>): string =>
  d.name.trim() || pinIcon(d.icon).label;

export type RecceCheck =
  { readonly ok: true; readonly value: PinValues } | { readonly ok: false; readonly error: string };

/** Check a recce hazard at `at` (null when the map has no position yet). */
export function checkRecceDraft(d: RecceDraft, at: LatLng | null): RecceCheck {
  if (
    !at ||
    !Number.isFinite(at.lat) ||
    !Number.isFinite(at.lng) ||
    Math.abs(at.lat) > 90 ||
    Math.abs(at.lng) > 180
  )
    return { ok: false, error: 'Move the map to the hazard first: it is placed at the cross in the middle.' };
  const name = recceName(d);
  if (name.length > PIN_NAME_MAX) return { ok: false, error: `Keep the name to ${PIN_NAME_MAX} characters.` };
  const notes = d.notes.trim();
  if (notes.length > PIN_DESCRIPTION_MAX)
    return { ok: false, error: `Keep the notes to ${PIN_DESCRIPTION_MAX} characters.` };
  const icon = pinIcon(d.icon);
  return {
    ok: true,
    value: {
      name,
      description: notes,
      lat: at.lat,
      lng: at.lng,
      icon: icon.key,
      colour: icon.defaultColour,
      hazard_map: '',
    },
  };
}

/** The `hazard_pin` row the phone sends through `sync_apply` (Teslyn Cloud stamps who placed it). */
export function reccePinRow(input: {
  readonly id: Uuid;
  readonly projectId: Uuid;
  readonly value: PinValues;
  readonly now: string;
}): HazardPinRecord {
  return {
    id: input.id,
    project_id: input.projectId,
    hazard_map: input.value.hazard_map,
    name: input.value.name,
    description: input.value.description,
    lat: input.value.lat,
    lng: input.value.lng,
    icon: input.value.icon,
    colour: input.value.colour,
    placed_by_name: '',
    created_at: input.now,
    updated_at: input.now,
    updated_by: null,
    deleted_at: null,
  };
}
